/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DefenceSecurityObligations.module.scss';

type Regime = {
  name: string;
  flags: string[];
  jurisdiction: string;
  reason: string;
  status: string;
  live?: boolean;
};

type Translate = (key: string) => string;

const getRegimes = (t: Translate): Regime[] => [
  {
    name: 'BIS Affiliates Rule',
    flags: ['us'],
    jurisdiction: 'US · 90 FR 47201',
    reason: t(
      "Extends Entity List, MEU and certain SDN licence requirements to entities owned 50% or more in aggregate by listed parties, with the strictest owner's requirement governing and the calculation resetting at each level below.",
    ),
    status: t('Returns 10 Nov 2026'),
  },
  {
    name: 'Entity List and MEU List',
    flags: ['us'],
    jurisdiction: 'US · EAR',
    reason: t(
      'Named-party licence requirements that apply today under the legally distinct standard, with red flags to be resolved where an unlisted subsidiary may divert to a listed parent.',
    ),
    status: t('In force'),
    live: true,
  },
  {
    name: 'OFAC 50 Percent Rule',
    flags: ['us'],
    jurisdiction: 'US · OFAC guidance',
    reason: t(
      'Entities owned 50% or more in aggregate by blocked persons are themselves blocked whether or not listed. The rule turns on ownership rather than control, so it can only be applied against resolved holdings.',
    ),
    status: t('In force'),
    live: true,
  },
  {
    name: t('Ownership and control test'),
    flags: ['gb'],
    jurisdiction: 'UK · OFSI',
    reason: t(
      'Ownership turns on more than 50% of shares or voting rights, or the right to appoint or remove a majority of the board, with a separate control limb. OFSI closed a call for evidence on the test in June 2026.',
    ),
    status: t('Under review'),
  },
  {
    name: 'National Security and Investment Act',
    flags: ['gb'],
    jurisdiction: 'UK · NSIA 2021',
    reason: t(
      'Mandatory notification for qualifying acquisitions in specified sensitive sectors, which requires the acquirer’s ownership chain to be traced and disclosed. Confirm current sector definitions with counsel.',
    ),
    status: t('In force'),
    live: true,
  },
  {
    name: t('Foreign investment screening'),
    flags: ['us', 'de'],
    jurisdiction: 'US CFIUS · EU 2019/452',
    reason: t(
      'National security review of foreign acquisitions turns on who ultimately controls the acquirer. Scope and procedure vary by member state and are subject to revision.',
    ),
    status: t('In force'),
    live: true,
  },
  {
    name: t('Export licensing'),
    flags: ['gb', 'us'],
    jurisdiction: 'UK ECJU · US ITAR, EAR',
    reason: t(
      'Licence applications and end-user assurances require the corporate identity and ownership of the consignee and end user to be established and evidenced.',
    ),
    status: t('In force'),
    live: true,
  },
];

export const DefenceSecurityObligations = () => {
  const { t } = useInt();
  const regimes = getRegimes(t);
  const rowsRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(rowsRef, { threshold: 0.1 });

  return (
    <Section background="#042354" className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('Several regimes. One test: who owns it.')}
          description={t(
            'Each of these turns on who owns a counterparty rather than what it is called, and none of them can be closed out with a screening list.',
          )}
          theme="dark"
        />
      </div>

      <div className={cn(styles.table, revealed && styles.tableOn)} ref={rowsRef}>
        <div className={cn(styles.row, styles.rowHead)}>
          <span>{t('Regime')}</span>
          <span>{t('Jurisdiction')}</span>
          <span>{t('Why it reaches ownership data')}</span>
          <span className={styles.statusHead}>{t('Status')}</span>
        </div>

        {regimes.map((regime, i) => (
          <div className={styles.row} style={{ '--d': `${i * 45}ms` } as CSSProperties} key={regime.name}>
            <h3 className={styles.name}>{regime.name}</h3>
            <span className={styles.jurisdiction}>
              {regime.flags.map((code) => (
                <Flag key={code} code={code} className={styles.flag} />
              ))}
              {regime.jurisdiction}
            </span>
            <p className={styles.reason}>{regime.reason}</p>
            <span className={cn(styles.badge, regime.live && styles.badgeLive)}>
              <i className={styles.dot} />
              {regime.status}
            </span>
          </div>
        ))}
      </div>

      <p className={styles.footnote}>
        {t(
          'The Affiliates Rule was suspended as part of a trade agreement and remains a live policy instrument. Treat the November date as current rather than settled, and re-check it before relying on it.',
        )}
      </p>
    </Section>
  );
};
